import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { 
  Users, 
  Download, 
  Search, 
  Calendar,
  DollarSign,
  AlertTriangle,
  TrendingUp,
  Eye,
  FileText,
  Phone,
  Mail,
  MapPin,
  CreditCard,
  ExternalLink
} from 'lucide-react';
import { apiService } from '../../services/api';

export const RelatorioClientesPage = () => {
  const [clientes, setClientes] = useState([]);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [relatorios, setRelatorios] = useState({});

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    aplicarFiltros();
  }, [clientes, filtro, filtroStatus]);

  const carregarDados = async () => {
    try {
      const clientesData = await apiService.getClientes();
      setClientes(clientesData);
      
      // Carregar dados complementares para cada cliente
      const relatoriosData = {};
      for (const cliente of clientesData) {
        relatoriosData[cliente.id] = await carregarRelatorioCliente(cliente.id);
      }
      setRelatorios(relatoriosData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarRelatorioCliente = async (clienteId) => {
    try {
      // Buscar dados históricos do cliente
      const [locacoes, pagamentos, cobrancas] = await Promise.all([
        apiService.getLocacoesPorCliente(clienteId),
        apiService.getPagamentosPorCliente(clienteId),
        apiService.getCobrancasPorCliente(clienteId)
      ]);

      // Calcular estatísticas
      const totalLocacoes = locacoes.length;
      const locacoesAtivas = locacoes.filter(l => l.status === 'ativa').length;
      const totalPago = pagamentos.reduce((sum, p) => sum + (p.valor_pago || 0), 0);
      const pagamentosAtrasados = pagamentos.filter(p => p.status === 'atrasado').length;
      const sinistros = cobrancas.filter(c => c.tipo === 'sinistro' || c.tipo === 'dano').length;
      const multas = cobrancas.filter(c => c.tipo === 'multa_transito').length;

      return {
        totalLocacoes,
        locacoesAtivas,
        totalPago,
        pagamentosAtrasados,
        sinistros,
        multas,
        historicoCompleto: {
          locacoes,
          pagamentos,
          cobrancas
        }
      };
    } catch (error) {
      console.error('Erro ao carregar relatório do cliente:', error);
      return {
        totalLocacoes: 0,
        locacoesAtivas: 0,
        totalPago: 0,
        pagamentosAtrasados: 0,
        sinistros: 0,
        multas: 0,
        historicoCompleto: { locacoes: [], pagamentos: [], cobrancas: [] }
      };
    }
  };

  const aplicarFiltros = () => {
    let resultado = clientes;

    // Filtro por nome/CPF
    if (filtro) {
      resultado = resultado.filter(cliente => 
        cliente.nome.toLowerCase().includes(filtro.toLowerCase()) ||
        cliente.cpf.includes(filtro) ||
        cliente.telefone.includes(filtro)
      );
    }

    // Filtro por status
    if (filtroStatus !== 'todos') {
      resultado = resultado.filter(cliente => {
        const relatorio = relatorios[cliente.id];
        if (!relatorio) return false;

        switch (filtroStatus) {
          case 'ativos':
            return relatorio.locacoesAtivas > 0;
          case 'inadimplentes':
            return relatorio.pagamentosAtrasados > 0;
          case 'com_sinistros':
            return relatorio.sinistros > 0;
          default:
            return true;
        }
      });
    }

    setClientesFiltrados(resultado);
  };

  const gerarRelatorioCliente = async (cliente) => {
    try {
      const relatorio = relatorios[cliente.id];
      if (!relatorio) return;

      const dados = {
        cliente: cliente,
        estatisticas: relatorio,
        historico: relatorio.historicoCompleto,
        dataGeracao: new Date().toISOString()
      };

      // Criar arquivo JSON para download
      const dataStr = JSON.stringify(dados, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_${cliente.nome.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      alert('Erro ao gerar relatório. Tente novamente.');
    }
  };

  const exportarTodosRelatorios = async () => {
    try {
      const todosRelatorios = clientesFiltrados.map(cliente => ({
        cliente: cliente,
        estatisticas: relatorios[cliente.id] || {},
        dataGeracao: new Date().toISOString()
      }));

      const dataStr = JSON.stringify(todosRelatorios, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio_completo_clientes_${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar relatórios:', error);
      alert('Erro ao exportar relatórios. Tente novamente.');
    }
  };

  const getStatusCliente = (cliente) => {
    const relatorio = relatorios[cliente.id];
    if (!relatorio) return { status: 'sem_dados', cor: 'bg-gray-100 text-gray-800' };

    if (relatorio.pagamentosAtrasados > 0) {
      return { status: 'inadimplente', cor: 'bg-red-100 text-red-800' };
    }
    if (relatorio.locacoesAtivas > 0) {
      return { status: 'ativo', cor: 'bg-green-100 text-green-800' };
    }
    return { status: 'inativo', cor: 'bg-gray-100 text-gray-800' };
  };

  if (loading) {
    return <div className="flex justify-center p-8">Carregando relatório de clientes...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Relatório de Clientes</h2>
        
        <Button 
          onClick={exportarTodosRelatorios}
          className="bg-green-600 hover:bg-green-700"
        >
          <Download className="h-4 w-4 mr-2" />
          Exportar Todos
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por nome, CPF ou telefone..."
                  value={filtro}
                  onChange={(e) => setFiltro(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="todos">Todos os Status</option>
                <option value="ativos">Clientes Ativos</option>
                <option value="inadimplentes">Inadimplentes</option>
                <option value="com_sinistros">Com Sinistros</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Resumo Geral */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">{clientesFiltrados.length}</p>
            <p className="text-sm text-gray-600">Total de Clientes</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="h-8 w-8 text-green-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {clientesFiltrados.filter(c => relatorios[c.id]?.locacoesAtivas > 0).length}
            </p>
            <p className="text-sm text-gray-600">Clientes Ativos</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              {clientesFiltrados.filter(c => relatorios[c.id]?.pagamentosAtrasados > 0).length}
            </p>
            <p className="text-sm text-gray-600">Inadimplentes</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <DollarSign className="h-8 w-8 text-yellow-500 mx-auto mb-2" />
            <p className="text-2xl font-bold">
              R$ {clientesFiltrados.reduce((total, c) => total + (relatorios[c.id]?.totalPago || 0), 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-gray-600">Total Arrecadado</p>
          </CardContent>
        </Card>
      </div>

      {/* Lista de Clientes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {clientesFiltrados.map((cliente) => {
          const relatorio = relatorios[cliente.id] || {};
          const statusInfo = getStatusCliente(cliente);
          
          return (
            <Card key={cliente.id} className="relative">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{cliente.nome}</CardTitle>
                    <p className="text-sm text-gray-600">{cliente.cpf}</p>
                  </div>
                  <Badge className={statusInfo.cor}>
                    {statusInfo.status}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-3">
                {/* Informações de Contato */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span>{cliente.telefone}</span>
                  </div>
                  {cliente.email && (
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span>{cliente.email}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span>{cliente.cidade}</span>
                  </div>
                </div>

                {/* Estatísticas */}
                <div className="grid grid-cols-2 gap-2 text-xs bg-gray-50 p-3 rounded">
                  <div className="text-center">
                    <p className="font-semibold text-blue-600">{relatorio.totalLocacoes || 0}</p>
                    <p className="text-gray-600">Locações</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-green-600">
                      R$ {(relatorio.totalPago || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-gray-600">Total Pago</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-red-600">{relatorio.pagamentosAtrasados || 0}</p>
                    <p className="text-gray-600">Atrasos</p>
                  </div>
                  <div className="text-center">
                    <p className="font-semibold text-orange-600">{relatorio.sinistros || 0}</p>
                    <p className="text-gray-600">Sinistros</p>
                  </div>
                </div>

                {/* Alertas */}
                {relatorio.pagamentosAtrasados > 0 && (
                  <Alert className="border-red-200 bg-red-50">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800 text-xs">
                      {relatorio.pagamentosAtrasados} pagamento(s) em atraso
                    </AlertDescription>
                  </Alert>
                )}

                {relatorio.sinistros > 0 && (
                  <Alert className="border-orange-200 bg-orange-50">
                    <AlertTriangle className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800 text-xs">
                      {relatorio.sinistros} sinistro(s) registrado(s)
                    </AlertDescription>
                  </Alert>
                )}

                {/* Botão de Download */}
                <Button 
                  onClick={() => gerarRelatorioCliente(cliente)}
                  variant="outline" 
                  size="sm" 
                  className="w-full"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Baixar Relatório
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {clientesFiltrados.length === 0 && (
        <Card className="text-center py-12">
          <CardContent>
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-600 mb-2">
              {filtro || filtroStatus !== 'todos' ? 'Nenhum cliente encontrado' : 'Nenhum cliente cadastrado'}
            </h3>
            <p className="text-gray-500">
              {filtro || filtroStatus !== 'todos' 
                ? 'Tente ajustar os filtros de busca' 
                : 'Cadastre o primeiro cliente para começar'
              }
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};